import { Box, Button, Container, Paper, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import LoadingIndicator from "ec/components/LoadingIndicator";
import { Field, Form, Formik } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import AppPage from ".";
import AppTextField from "../forms/AppTextField";
import { useChangeUser } from "../userHooks";

const normalizeErrorMessage = (message) => (Array.isArray(message) ? message.join(", ") : message);

const splitErrors = (errors = {}) =>
  Object.entries(errors).reduce(
    (result, [field, message]) => {
      if (field === "base") {
        return {
          ...result,
          formError: normalizeErrorMessage(message),
        };
      }

      return {
        ...result,
        fieldErrors: {
          ...result.fieldErrors,
          [field]: normalizeErrorMessage(message),
        },
      };
    },
    { fieldErrors: {}, formError: undefined },
  );

const ChangeAccountInfo = () => {
  const [updateUser, loading] = useChangeUser();
  const profile = useSelector((state) => state.userProfile.data);
  const initialValues = useMemo(() => {
    const { email, first_name, last_name } = profile;
    return { email, first_name, last_name };
  }, [profile]);

  const [success, setSuccess] = useState(false);

  const handleSubmit = useCallback(
    (values, { setErrors, setStatus, setSubmitting }) => {
      setStatus(undefined);
      updateUser(values)
        .then((data) => {
          if (data && data.errors) {
            const { fieldErrors, formError } = splitErrors(data.errors);
            setErrors(fieldErrors);
            setStatus(formError);
          } else {
            setSuccess(true);
          }
        })
        .finally(() => setSubmitting(false));
    },
    [updateUser],
  );

  const navigate = useNavigate();
  const goHome = useCallback(() => navigate("/"), [navigate]);

  if (success) {
    return (
      <AppPage docTitle="Account Info Successfully Updated">
        <Container maxWidth="xs">
          <Box sx={{ my: 5 }}>
            <Alert
              variant="filled"
              severity="success"
              action={
                <Button variant="outlined" color="inherit" size="small" onClick={goHome}>
                  Continue
                </Button>
              }
            >
              Changes Changed
            </Alert>
          </Box>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage docTitle="Account Info">
      <Container maxWidth="xs">
        <Box sx={{ my: 5 }}>
          {!profile && <LoadingIndicator />}
          <Paper variant="outlined">
            <Box sx={{ p: 4 }}>
              <Formik
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}
              >
                {({ status }) => (
                  <Form>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h5" component="h1">
                        Update Your Account Info
                      </Typography>
                    </Box>
                    {status && (
                      <Box sx={{ mb: 2 }}>
                        <Alert severity="error">{status}</Alert>
                      </Box>
                    )}
                    <Box sx={{ mb: 2 }}>
                      <Field name="email" label="Email" as={AppTextField} type="email" />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Field name="first_name" label="First Name" as={AppTextField} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Field name="last_name" label="Last Name" as={AppTextField} />
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Button onClick={goHome}>Cancel</Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={loading}
                      >
                        Save
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </Paper>
        </Box>
      </Container>
    </AppPage>
  );
};

export default ChangeAccountInfo;
